/* StatisticsPanel — Automated Messages → Statistics sub-tab.

   Phase 1: New-Subscription welcome-sequence performance. The funnel is a
   cohort over `subscribed_at` — subs that started a sequence inside the chosen
   window — so every step (messaged → replied → completed) is measured for that
   same set of fans. The bigdayz.new subscription coordinator writes the source
   rows; this side only reads and rolls them up (see subscription_welcome_stats_db).

   The period selector goes down to the hour, so windows are ISO-8601 instants,
   not calendar days — "all time" sends no bounds. Online-opener stats land in a
   later phase; this panel is scoped to the subscription trigger. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { WelcomeStatsTotals } from '../../api/client';
import { FeatherIcon } from '../FeatherIcon';
import { OnlineStatsPanel } from './OnlineStatsPanel';
import {
  Card,
  Funnel,
  Kpi,
  MetricRow,
  STAT_BASE,
  ZeroState,
  fmt,
  pct,
  ratio,
  type BarItem,
} from '../Statistics/statsKit';

interface Props {
  accountId: string;
}

type View = 'subscriptions' | 'online';

/** Statistics sub-tab shell: switches between the two message flows. Subscriptions
    (welcome sequences) has a full period selector; Online (openers) is all-time +
    live snapshot — see each view for why. */
export function StatisticsPanel({ accountId }: Props) {
  const { t } = useTranslation('automatedMessages');
  const [view, setView] = useState<View>('subscriptions');
  const views: { id: View; label: string; icon: string }[] = [
    { id: 'subscriptions', label: t('stats.view.subscriptions'), icon: 'user-plus' },
    { id: 'online', label: t('stats.view.online'), icon: 'radio' },
  ];

  return (
    <div style={{ ...STAT_BASE, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="mobile-scroll-row" style={{ display: 'inline-flex', alignSelf: 'flex-start', background: 'var(--card-2)', borderRadius: 12, padding: 4, gap: 4, border: '1px solid var(--line)' }}>
        {views.map((v) => {
          const on = view === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              style={{
                border: 'none',
                cursor: 'pointer',
                borderRadius: 9,
                padding: '9px 16px',
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
                transition: 'all .15s',
                background: on ? 'var(--accent)' : 'transparent',
                color: on ? 'var(--accent-on)' : 'var(--muted)',
                boxShadow: on ? '0 4px 14px -6px var(--accent)' : 'none',
              }}
            >
              <FeatherIcon name={v.icon} size={14} />
              {v.label}
            </button>
          );
        })}
      </div>

      {view === 'subscriptions' ? <WelcomeStatsView accountId={accountId} /> : <OnlineStatsPanel accountId={accountId} />}
    </div>
  );
}

type Preset = 'hour' | 'day' | 'week' | 'month' | 'all' | 'custom';
const PRESETS: Preset[] = ['hour', 'day', 'week', 'month', 'all'];

const PRESET_MS: Record<Exclude<Preset, 'all' | 'custom'>, number> = {
  hour: 3600_000,
  day: 86_400_000,
  week: 7 * 86_400_000,
  month: 30 * 86_400_000,
};

/** `<input type="datetime-local">` wants "YYYY-MM-DDTHH:mm" in local time. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function WelcomeStatsView({ accountId }: Props) {
  const { t } = useTranslation('automatedMessages');

  const [preset, setPreset] = useState<Preset>('week');
  // Custom-range inputs (local time); only read when preset === 'custom'.
  const [customFrom, setCustomFrom] = useState(() => toLocalInput(new Date(Date.now() - PRESET_MS.week)));
  const [customTo, setCustomTo] = useState(() => toLocalInput(new Date()));

  const [totals, setTotals] = useState<WelcomeStatsTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Bumped by Apply so a custom range only refetches on demand, not per keystroke.
  const [applyTick, setApplyTick] = useState(0);

  const range = useMemo((): { start?: string; end?: string; invalid: boolean } => {
    if (preset === 'all') return { invalid: false };
    if (preset === 'custom') {
      const s = customFrom ? new Date(customFrom) : null;
      const e = customTo ? new Date(customTo) : null;
      if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) {
        return { invalid: true };
      }
      return { start: s.toISOString(), end: e.toISOString(), invalid: false };
    }
    const now = Date.now();
    return { start: new Date(now - PRESET_MS[preset]).toISOString(), end: new Date(now).toISOString(), invalid: false };
    // applyTick re-anchors "now" for the relative presets when the user re-picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customFrom, customTo, applyTick]);

  const load = useCallback(async () => {
    if (range.invalid) return;
    setLoading(true);
    setError(false);
    try {
      const res = await api.getSubscriptionWelcomeStats(accountId, range.start, range.end);
      setTotals(res.totals);
    } catch {
      setError(true);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, range.start, range.end, range.invalid]);

  useEffect(() => {
    void load();
  }, [load]);

  const reasonLabel = (reason: string): string => {
    const key = `stats.reasons.${reason}`;
    const translated = t(key);
    // i18next returns the key back when there's no match — humanize the raw value.
    if (translated !== key) return translated;
    return reason.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  };

  const started = totals?.sequences_started ?? 0;
  const replyRate = totals ? ratio(totals.fans_replied, totals.sequences_started) * 100 : 0;
  const completionRate = totals ? ratio(totals.completed, totals.sequences_started) * 100 : 0;

  const reasonItems: BarItem[] = (totals?.interruption_reasons ?? []).map((r) => ({
    label: reasonLabel(r.reason),
    n: r.count,
    icon: 'x-circle',
  }));

  return (
    <div style={{ ...STAT_BASE, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* heading */}
      <div>
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.6,
          }}
        >
          {t('stats.title')}
        </h2>
        <p style={{ margin: '6px 0 0', fontSize: 14, fontWeight: 500, color: 'var(--muted)' }}>{t('stats.subtitle')}</p>
      </div>

      {/* period picker */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 14,
          flexWrap: 'wrap',
          padding: '14px 16px',
          borderRadius: 'var(--rad-md)',
          background: 'var(--card)',
          border: '1px solid var(--line)',
        }}
      >
        <div className="mobile-scroll-row" style={{ display: 'inline-flex', background: 'var(--card-2)', borderRadius: 11, padding: 3, gap: 3, border: '1px solid var(--line)' }}>
          {PRESETS.map((p) => {
            const on = preset === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => { setPreset(p); setApplyTick((n) => n + 1); }}
                style={{
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '9px 15px',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  transition: 'all .15s',
                  background: on ? 'var(--accent)' : 'transparent',
                  color: on ? 'var(--accent-on)' : 'var(--muted)',
                  boxShadow: on ? '0 4px 14px -6px var(--accent)' : 'none',
                }}
              >
                {t(`stats.period.${p}`)}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setPreset('custom')}
            style={{
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
              padding: '9px 15px',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              transition: 'all .15s',
              background: preset === 'custom' ? 'var(--accent)' : 'transparent',
              color: preset === 'custom' ? 'var(--accent-on)' : 'var(--muted)',
              boxShadow: preset === 'custom' ? '0 4px 14px -6px var(--accent)' : 'none',
            }}
          >
            {t('stats.period.custom')}
          </button>
        </div>

        {preset === 'custom' && (
          <>
            <span style={{ width: 1, height: 30, background: 'var(--line)' }} />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--faint)' }}>{t('stats.customRange.from')}</span>
              <input
                type="datetime-local"
                value={customFrom}
                max={customTo}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 9, padding: '8px 10px', color: 'var(--text)', colorScheme: 'dark', fontSize: 13 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--faint)' }}>{t('stats.customRange.to')}</span>
              <input
                type="datetime-local"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--line)', borderRadius: 9, padding: '8px 10px', color: 'var(--text)', colorScheme: 'dark', fontSize: 13 }}
              />
            </label>
            <button
              type="button"
              onClick={() => setApplyTick((n) => n + 1)}
              disabled={loading || range.invalid}
              style={{
                border: 'none',
                cursor: loading || range.invalid ? 'not-allowed' : 'pointer',
                borderRadius: 9,
                padding: '9px 18px',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                background: 'var(--accent)',
                color: 'var(--accent-on)',
                opacity: loading || range.invalid ? 0.5 : 1,
              }}
            >
              {t('stats.customRange.apply')}
            </button>
          </>
        )}
      </div>

      {error ? (
        <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--faint)' }}>
          <FeatherIcon name="alert-triangle" size={22} />
          <div style={{ marginTop: 8 }}>{t('stats.error')}</div>
        </div>
      ) : !loading && started === 0 ? (
        <ZeroState text={`${t('stats.zero.title')} — ${t('stats.zero.body')}`} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          {/* hero KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
            <Kpi icon="user-plus" tone="accent" label={t('stats.kpi.newSubs')} value={fmt(started)} sub={t('stats.kpi.newSubsHint')} />
            <Kpi icon="message-circle" tone="ok" label={t('stats.kpi.replyRate')} value={pct(replyRate)} sub={t('stats.kpi.replyRateHint')} />
            <Kpi icon="check-circle" tone="accent" label={t('stats.kpi.completionRate')} value={pct(completionRate)} sub={t('stats.kpi.completionRateHint')} />
            <Kpi icon="send" tone="accent" label={t('stats.kpi.welcomeMessages')} value={fmt(totals?.welcome_messages_sent ?? 0)} />
          </div>

          {/* Funnel — strictly monotonic: replied ⊆ messaged ⊆ started. Completion is
              a separate outcome (a sequence can finish with no reply), so it lives in
              the KPIs and breakdown, not here. */}
          <Card icon="filter" title={t('stats.funnel.title')}>
            <Funnel
              steps={[
                { label: t('stats.funnel.started'), n: started, hint: t('stats.funnel.startedHint'), tone: 'accent' },
                { label: t('stats.funnel.messaged'), n: totals?.sequences_with_send ?? 0, hint: t('stats.funnel.messagedHint'), tone: 'accent' },
                { label: t('stats.funnel.replied'), n: totals?.fans_replied ?? 0, hint: t('stats.funnel.repliedHint'), tone: 'ok' },
              ]}
            />
          </Card>

          {/* breakdown + interruptions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            <Card icon="pie-chart" title={t('stats.detail.title')}>
              <div>
                <MetricRow label={t('stats.detail.firstTime')} value={totals?.first_time_subs ?? 0} of={started} tone="accent" />
                <MetricRow label={t('stats.detail.resub')} value={totals?.resubscriptions ?? 0} of={started} tone="accent" />
                <MetricRow label={t('stats.detail.inProgress')} value={totals?.in_progress ?? 0} of={started} tone="warn" />
                <MetricRow label={t('stats.detail.interrupted')} value={totals?.interrupted ?? 0} of={started} tone="warn" />
                <MetricRow label={t('stats.detail.failed')} value={totals?.failed ?? 0} of={started} tone="warn" />
                {/* An average, not a count — render the decimal directly rather than through fmt(). */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '10px 0', borderTop: '1px solid var(--line-2)' }}>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 550, color: 'var(--muted)' }}>{t('stats.detail.avgBeforeReply')}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: (totals?.avg_messages_before_reply ?? 0) === 0 ? 'var(--faint)' : 'var(--text)' }}>
                    {(totals?.avg_messages_before_reply ?? 0).toFixed(1)}
                  </span>
                </div>
              </div>
            </Card>

            <Card icon="alert-octagon" tone="warn" title={t('stats.interruptions.title')}>
              {reasonItems.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {reasonItems.map((it) => {
                    const total = totals?.interrupted ?? 0;
                    const share = total > 0 ? (it.n / total) * 100 : 0;
                    return (
                      <div key={it.label} style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '11px 0', borderTop: '1px solid var(--line-2)' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                          {/* Full reason text — wraps instead of truncating. */}
                          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>{it.label}</span>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 14.5, fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(it.n)}</span>
                          <span style={{ width: 40, textAlign: 'right', fontSize: 11.5, fontWeight: 600, color: 'var(--faint)', fontFamily: 'var(--mono)' }}>{Math.round(share)}%</span>
                        </div>
                        <span style={{ height: 6, borderRadius: 4, background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', overflow: 'hidden', display: 'block' }}>
                          <span style={{ display: 'block', height: '100%', width: Math.max(share, it.n ? 2 : 0) + '%', borderRadius: 4, background: 'var(--warn)', opacity: 0.85 }} />
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 550, color: 'var(--faint)' }}>{t('stats.interruptions.empty')}</p>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
